/**
 * One paper's upload on the PHONE (PROD-1511): the record wizard shows one of
 * these per paper the school may hold, the kitchen's Orders sheet and the
 * production record, so nobody has to guess which sheet "each filled sheet"
 * means.
 *
 * Top to bottom, one column: the paper's name and photo count, one line on
 * which paper it is and who fills it, the drawing with what it fills
 * (`SheetPreview`), the photos queued so far with an Add tile, and an optional
 * note (which deliveries this record covers).
 *
 * The Add tile opens ONE picker with no `capture` hint: the phone then offers
 * the camera and the library itself, which is one control where the old
 * screen had two. Thumbnails are object URLs, revoked whenever the queue
 * changes or the card unmounts.
 */

import { cn } from '@ordo/ui/cn'
import { Camera, ClipboardList, type LucideIcon, Truck, X } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'

import { SheetPreview } from '@/components/production/sheets/sheet-preview'
import { photoKey } from '@/lib/production-records/mobile-wizard'
import { SHEET_PAPER_NAME, type SheetPaper } from '@/lib/production-records/sheet-papers'

/** One line on which paper each upload wants, and who fills it in. */
export const SHEET_PAPER_DESCRIPTION: Record<SheetPaper, string> = {
  delivery:
    'The Orders sheet the kitchen sent with the food. Your staff write the drop-off temperatures, tick Received and sign at the bottom.',
  record: 'The USDA form printed from Production Records, marked PREP COPY.',
}

export const SHEET_PAPER_ICON: Record<SheetPaper, LucideIcon> = {
  delivery: Truck,
  record: ClipboardList,
}

/** `2 photos`, `1 photo`. */
export function photoCountLabel(count: number): string {
  return count === 1 ? '1 photo' : `${count} photos`
}

/** Object URLs for queued photos, revoked when the queue changes or the owner unmounts. */
export function usePhotoPreviews(photos: readonly File[]): Array<{ key: string; url: string }> {
  const previews = useMemo(
    () => photos.map((file) => ({ key: photoKey(file), url: URL.createObjectURL(file) })),
    [photos],
  )
  useEffect(() => {
    return () => {
      for (const preview of previews) {
        URL.revokeObjectURL(preview.url)
      }
    }
  }, [previews])
  return previews
}

export interface SheetUploadCardProps {
  paper: SheetPaper
  photos: readonly File[]
  onAdd: (files: File[]) => void
  onRemove: (key: string) => void
  /** Appended to the paper's own line, e.g. "One page per mealtime: Breakfast and Lunch." */
  detail?: string | null
  /** Under the photos, e.g. which deliveries this record covers. */
  note?: string | null
  /** The day's photo cap is reached: the Add tile stops taking photos. */
  full?: boolean
  disabled?: boolean
}

export function SheetUploadCard({
  paper,
  photos,
  onAdd,
  onRemove,
  detail = null,
  note = null,
  full = false,
  disabled = false,
}: SheetUploadCardProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const previews = usePhotoPreviews(photos)
  const Icon = SHEET_PAPER_ICON[paper]
  const name = SHEET_PAPER_NAME[paper]
  return (
    <section
      data-sheet-upload={paper}
      aria-label={name}
      className="flex flex-col gap-3.5 rounded-[14px] border bg-card p-4">
      <div className="flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-muted text-foreground/80">
          <Icon className="size-[22px]" aria-hidden="true" />
        </span>
        <h3 className="min-w-0 flex-1 text-[17px] font-semibold leading-6">{name}</h3>
        {photos.length > 0 ? (
          <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium leading-[18px] text-foreground/80">
            {photoCountLabel(photos.length)}
          </span>
        ) : null}
      </div>
      <p className="text-sm leading-[21px] text-foreground/80">
        {SHEET_PAPER_DESCRIPTION[paper]}
        {detail ? ` ${detail}` : null}
      </p>
      <SheetPreview paper={paper} />
      <ul className="grid grid-cols-3 gap-3 pt-1.5" aria-label={`${name} photos`}>
        {previews.map((preview, i) => (
          <li key={preview.key} className="relative">
            <img
              src={preview.url}
              alt={`${name} photo ${i + 1}`}
              className="h-[84px] w-full rounded-lg border bg-muted object-cover"
            />
            <button
              type="button"
              aria-label={`Remove ${name.toLowerCase()} photo ${i + 1}`}
              disabled={disabled}
              className="absolute -right-2 -top-2 flex size-8 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm"
              onClick={() => onRemove(preview.key)}>
              <X className="size-4" aria-hidden="true" />
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            disabled={disabled || full}
            aria-label={`Add ${name.toLowerCase()} photos`}
            className={cn(
              'flex h-[84px] w-full flex-col items-center justify-center gap-1 rounded-lg border-[1.5px] border-dashed border-muted-foreground/50 bg-background text-[13px] font-medium',
              'disabled:opacity-50',
            )}
            onClick={() => inputRef.current?.click()}>
            <Camera className="size-[22px]" aria-hidden="true" />
            Add photo
          </button>
        </li>
      </ul>
      {note ? <p className="text-[13px] leading-[19px] text-muted-foreground">{note}</p> : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid={`sheet-upload-input-${paper}`}
        onChange={(e) => {
          onAdd(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />
    </section>
  )
}
